import * as React from "react";
import DatePicker, {
  ReactDatePickerProps,
  CalendarContainer,
} from "react-datepicker";
import {
  chakra,
  InputGroup,
  InputRightElement,
  Icon,
  Input,
  useFormControl,
} from "@chakra-ui/react";
import {
  add,
  format as formatDate,
  getYear,
  isAfter,
  isBefore,
  isValid,
  parse,
  startOfDay,
} from "date-fns";
import { EmrCalendar } from "@medstonetech/slate-icons";
import { CalendarTimeInput } from "./CalendarTimeInput";
import InputMask from "react-input-mask";
import { CalendarHeader } from "../calendar-header";

import "react-datepicker/dist/react-datepicker.css";
import "./calendarInput.css";

const CkCalendarContainer = chakra(CalendarContainer);

type CalendarSelection = {
  date: Date;
  revision: number;
};

type CalendarInputCustomProps = {
  onShowCalendar?: React.MouseEventHandler<SVGElement>;
  onClearInput?: () => void;
  onResetInput?: (value: string | undefined) => void;
  calendarSelection?: CalendarSelection;
};

type InnerProps = {
  minDate?: Date;
  maxDate?: Date;
  showIcon?: boolean;
  showCalendar?: boolean;
};

const getInputValue = (
  event: React.SyntheticEvent<Element> | undefined
): string | undefined => {
  const target = event?.target;
  return target instanceof HTMLInputElement ? target.value : undefined;
};

type CalendarInputProps = InnerProps &
  Omit<ReactDatePickerProps, keyof InnerProps>;

type CustomInputProps = React.ComponentProps<typeof Input> & {
  showIcon?: boolean;
  onShowCalendar?: React.MouseEventHandler<SVGElement>;
};

const CustomInput = React.forwardRef<HTMLInputElement, CustomInputProps>(
  (props, ref) => {
    const { showIcon, onShowCalendar, ...restProps } = props;

    return (
      <InputGroup>
        <Input ref={ref} {...restProps} />
        {showIcon ? (
          <InputRightElement>
            <Icon
              onClick={onShowCalendar}
              as={EmrCalendar}
              color="gray.700"
              fontSize="1.25rem"
            />
          </InputRightElement>
        ) : null}
      </InputGroup>
    );
  }
);

CustomInput.displayName = "CalendarCustomInput";

const CalendarInput = React.forwardRef<DatePicker, CalendarInputProps>(
  (props, ref) => {
    const {
      minDate = new Date(1900, 0, 1),
      maxDate = add(new Date(), { years: 10 }),
      dateFormat = "MM-dd-yyyy",
      showIcon = true,
      showCalendar = false,
    } = props;

    const formControl = useFormControl(props);
    const [isCalendarOpen, setIsCalendarOpen] = React.useState(false);
    const [inputValueOverride, setInputValueOverride] = React.useState<
      string | undefined
    >();
    const [calendarSelection, setCalendarSelection] = React.useState<
      CalendarSelection | undefined
    >();
    const controlledSelectedValue =
      props.selected === null ? null : props.selected?.getTime();
    const previousSelectedValueRef = React.useRef(controlledSelectedValue);
    const previousValueRef = React.useRef(props.value);

    const clearInputValueOverride = React.useCallback(() => {
      setInputValueOverride(undefined);
    }, []);

    React.useEffect(() => {
      const selectedValueChanged =
        controlledSelectedValue !== previousSelectedValueRef.current;
      const valueChanged = props.value !== previousValueRef.current;

      if (!selectedValueChanged && !valueChanged) return;

      previousSelectedValueRef.current = controlledSelectedValue;
      previousValueRef.current = props.value;
      clearInputValueOverride();
    }, [clearInputValueOverride, controlledSelectedValue, props.value]);

    const handleInputClick = React.useCallback(() => {
      if (showCalendar) {
        setIsCalendarOpen(true);
      }
    }, [showCalendar]);

    const handleClearInput = React.useCallback(() => {
      setInputValueOverride("");
    }, []);

    const handleResetInput = React.useCallback(
      (nextValue: string | undefined) => {
        setInputValueOverride(nextValue ?? "");
      },
      []
    );

    const customInputEl = React.useMemo(() => {
      if (props.customInput) {
        return React.isValidElement(props.customInput)
          ? React.cloneElement(
              props.customInput as React.ReactElement<CalendarInputCustomProps>,
              {
                onShowCalendar: handleInputClick,
                onClearInput: handleClearInput,
                onResetInput: handleResetInput,
                calendarSelection,
              }
            )
          : props.customInput;
      }

      return (
        <CustomInput
          color="blue"
          borderColor={props.selected ? "blue" : "gray.450"}
          showIcon={showIcon}
          onShowCalendar={handleInputClick}
        />
      );
    }, [
      calendarSelection,
      handleClearInput,
      handleInputClick,
      handleResetInput,
      props.customInput,
      props.selected,
      showIcon,
    ]);

    const handleDatePickerChange: ReactDatePickerProps["onChange"] = (
      date,
      event
    ) => {
      const targetValue = getInputValue(event);

      if (targetValue === "") {
        props.onChange?.(null, event);
        return;
      }

      const nextDate = date && isValid(date) ? date : null;
      props.onChange?.(nextDate, event);
    };

    const handleDatePickerChangeRaw = (
      event: React.SyntheticEvent<HTMLInputElement>
    ) => {
      if (!(event.currentTarget instanceof HTMLInputElement)) return;

      const targetValue = event.currentTarget.value;
      if (targetValue === "") {
        setInputValueOverride(undefined);
        props.onChange?.(null, event);
        return;
      }

      const dateFormats = Array.isArray(dateFormat) ? dateFormat : [dateFormat];
      const isValidInput = dateFormats.some((formatPattern) => {
        if (typeof formatPattern !== "string") return false;

        try {
          const parsedDate = parse(targetValue, formatPattern, new Date());
          const normalizedValue = formatDate(parsedDate, formatPattern);
          const parsedDay = startOfDay(parsedDate);

          return (
            isValid(parsedDate) &&
            normalizedValue === targetValue &&
            !isBefore(parsedDay, startOfDay(minDate)) &&
            !isAfter(parsedDay, startOfDay(maxDate))
          );
        } catch {
          return false;
        }
      });

      if (!isValidInput) return;
      setInputValueOverride(undefined);
    };

    return (
      <DatePicker
        ref={ref}
        {...props}
        {...formControl}
        minDate={minDate}
        maxDate={maxDate}
        onChange={handleDatePickerChange}
        onChangeRaw={handleDatePickerChangeRaw}
        preventOpenOnFocus
        customTimeInput={<CalendarTimeInput />}
        timeInputLabel=""
        dateFormat={dateFormat}
        dateFormatCalendar="MM-dd-yyyy"
        renderCustomHeader={CalendarHeader}
        customInput={customInputEl}
        value={inputValueOverride ?? props.value}
        showPopperArrow={false}
        formatWeekDay={(nameOfDay) => {
          return nameOfDay.substring(0, 3).toUpperCase();
        }}
        calendarContainer={({ children, ...calendarProps }) => (
          <CkCalendarContainer
            {...calendarProps}
            fontFamily="body"
            border="1px solid #D1D1D6"
            boxShadow="0px 4px 4px rgba(50, 50, 71, 0.08), 0px 4px 8px rgba(50, 50, 71, 0.06)"
            borderRadius="13px"
            fontSize="unset"
            padding="1"
          >
            {children}
          </CkCalendarContainer>
        )}
        open={isCalendarOpen}
        onClickOutside={() => setIsCalendarOpen(false)}
        onSelect={(date, event) => {
          setInputValueOverride(undefined);
          setCalendarSelection((currentSelection) => ({
            date,
            revision: (currentSelection?.revision ?? 0) + 1,
          }));
          setIsCalendarOpen(false);
          handleDatePickerChange(date, event);
        }}
      />
    );
  }
);

CalendarInput.displayName = "CalendarInput";

type WatchControl = {
  watchInternal?: (name?: string) => string | Date | undefined;
  subjectsRef?: React.MutableRefObject<{
    control: {
      subscribe: (observer: { next: (event: ControlEvent) => void }) => {
        unsubscribe: () => void;
      };
    };
  }>;
};

type ControlEvent = {
  name?: string;
};

type ResetSignal = {
  revision: number;
  value: string | undefined;
};

const normalizeMaskedValue = (
  value: string | Date | undefined
): string | undefined => {
  if (value instanceof Date) {
    return isValid(value) ? formatDate(value, maskedDateFormat) : undefined;
  }

  return value;
};

const useResetSignal = (
  control: WatchControl | undefined,
  fieldName: string | undefined
): ResetSignal => {
  const [signal, setSignal] = React.useState<ResetSignal>(() => ({
    revision: 0,
    value: normalizeMaskedValue(control?.watchInternal?.(fieldName)),
  }));

  React.useEffect(() => {
    const subject = control?.subjectsRef?.current.control;
    if (!subject) return;

    const subscription = subject.subscribe({
      next: ({ name }) => {
        if (name !== undefined) return;

        setSignal((currentSignal) => ({
          revision: currentSignal.revision + 1,
          value: normalizeMaskedValue(control.watchInternal?.(fieldName)),
        }));
      },
    });

    return () => subscription.unsubscribe();
  }, [control, fieldName]);

  return signal;
};

const maskedDateFormat = "MM-dd-yyyy";
const maskedDateMin = new Date(1900, 0, 1);

type MaskedCalendarProps = {
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onEdit?: React.ChangeEventHandler<HTMLInputElement>;
  onInvalidInput?: React.ChangeEventHandler<HTMLInputElement>;
  onShowCalendar?: React.MouseEventHandler<SVGElement>;
  onClearInput?: () => void;
  onResetInput?: (value: string | undefined) => void;
  value?: string;
  name?: string;
  control?: WatchControl;
  minDate?: Date;
  maxDate?: Date;
  calendarSelection?: CalendarSelection;
} & Omit<React.ComponentProps<typeof Input>, "onChange" | "value" | "name">;

const MaskedCalendarInput = React.forwardRef<
  HTMLInputElement,
  MaskedCalendarProps
>((props, ref) => {
  const {
    onChange: onChangeProp,
    onEdit,
    onInvalidInput,
    onShowCalendar,
    onClearInput,
    onResetInput,
    value,
    name,
    control,
    minDate,
    maxDate,
    calendarSelection,
    ...restProps
  } = props;

  const defaultValue = normalizeMaskedValue(control?.watchInternal?.(name));
  const resetSignal = useResetSignal(control, name);

  const [controlledValue, setControlledValue] = React.useState<
    string | undefined
  >(value ?? defaultValue);
  const [inputValueOverride, setInputValueOverride] = React.useState<
    string | undefined
  >();
  const [isTyping, setIsTyping] = React.useState(false);
  const previousValueRef = React.useRef(value);
  const previousDefaultValueRef = React.useRef(defaultValue);
  const lastAppliedResetRevisionRef = React.useRef(0);

  React.useEffect(() => {
    if (value === previousValueRef.current) return;

    previousValueRef.current = value;
    setInputValueOverride(undefined);
    setControlledValue(value);
    setIsTyping(false);
  }, [value]);

  React.useEffect(() => {
    if (defaultValue === previousDefaultValueRef.current) return;

    previousDefaultValueRef.current = defaultValue;
    if (!isTyping || defaultValue === undefined || defaultValue === "") {
      setInputValueOverride(undefined);
      setControlledValue(defaultValue);
      setIsTyping(false);
    }
  }, [defaultValue, isTyping]);

  React.useEffect(() => {
    if (resetSignal.revision <= lastAppliedResetRevisionRef.current) return;

    lastAppliedResetRevisionRef.current = resetSignal.revision;
    setInputValueOverride(undefined);
    setControlledValue(resetSignal.value);
    setIsTyping(false);
    onResetInput?.(resetSignal.value);
  }, [onResetInput, resetSignal.revision, resetSignal.value]);

  React.useEffect(() => {
    if (!calendarSelection) return;

    setInputValueOverride(undefined);
    setControlledValue(formatDate(calendarSelection.date, maskedDateFormat));
    setIsTyping(false);
  }, [calendarSelection]);

  const validateInput = (inputValue: string): boolean => {
    if (inputValue.length === 0) return true;

    const [month = "", day = "", year = ""] = inputValue.split("-");
    if (
      inputValue.split("-").length > 3 ||
      !/^\d*$/.test(month) ||
      !/^\d*$/.test(day) ||
      !/^\d*$/.test(year) ||
      month.length > 2 ||
      day.length > 2 ||
      year.length > 4
    ) {
      return false;
    }

    const monthValue = Number(month);
    const dayValue = Number(day);
    const yearValue = Number(year);
    const isValidMonth =
      month.length < 2 ? monthValue <= 1 : monthValue >= 1 && monthValue <= 12;
    const isValidDay =
      day.length < 2 ? dayValue <= 3 : dayValue >= 1 && dayValue <= 31;
    const minYear = getYear(minDate ?? maskedDateMin);
    const maxYear = getYear(maxDate ?? new Date());
    const isValidYear =
      year.length < 4 || (yearValue >= minYear && yearValue <= maxYear);

    if (!isValidMonth || !isValidDay || !isValidYear) return false;
    if (inputValue.length !== 10) return true;

    try {
      const parsedDate = parse(inputValue, maskedDateFormat, new Date());
      const normalizedValue = formatDate(parsedDate, maskedDateFormat);
      const parsedDay = startOfDay(parsedDate);

      return (
        isValid(parsedDate) &&
        normalizedValue === inputValue &&
        !isBefore(parsedDay, startOfDay(minDate ?? maskedDateMin)) &&
        !isAfter(parsedDay, startOfDay(maxDate ?? new Date()))
      );
    } catch {
      return false;
    }
  };

  const onChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const inputValue = event.target.value;
    const isInputValid = validateInput(inputValue);
    const isCompleteInput = inputValue.length === 10;

    onEdit?.(event);

    if (inputValue.length === 0) {
      setInputValueOverride(undefined);
      setControlledValue(inputValue);
      setIsTyping(false);
      onChangeProp?.(event);
      return;
    }

    if (!isInputValid) {
      if (isCompleteInput) {
        setInputValueOverride("");
        setControlledValue("");
        setIsTyping(false);
        onClearInput?.();
        onInvalidInput?.(event);
      }
      return;
    }

    setInputValueOverride(undefined);
    setControlledValue(inputValue);
    setIsTyping(true);
    if (isCompleteInput) {
      onChangeProp?.(event);
    }
  };

  return (
    <InputGroup>
      <Input
        ref={ref}
        as={InputMask}
        mask="99-99-9999"
        maskChar={null}
        value={
          inputValueOverride ??
          (isTyping ? controlledValue ?? "" : value ?? controlledValue ?? "")
        }
        onChange={onChange}
        placeholder="MM-DD-YYYY"
        {...restProps}
      />
      <InputRightElement>
        <Icon
          as={EmrCalendar}
          color="gray.700"
          fontSize="1.25rem"
          onClick={onShowCalendar}
        />
      </InputRightElement>
    </InputGroup>
  );
});

MaskedCalendarInput.displayName = "MaskedCalendarInput";

export { CalendarInput, MaskedCalendarInput };
export type { CalendarInputProps };
